// Status and role union types — sourced verbatim from
// requirements/technical/models/Price_Rescue_DynamoDB_Models_and_Repositories_v2.0.md §4.

import type { CATALOG_REPAIR_JOB_TYPE } from "./catalog-repair.js";
import type { MAPPING_CHECK_JOB_TYPE } from "./product-monitoring.js";

export const STORE_STATUS_UNINSTALLED = "UNINSTALLED";
export const STORE_STATUS_PENDING = "PENDING";
export const STORE_STATUS_ACTIVE = "ACTIVE";

export const STORE_STATUSES = [
  "INSTALLING",
  STORE_STATUS_PENDING,
  STORE_STATUS_ACTIVE,
  "PAUSED",
  "SUSPENDED",
  "ERROR",
  STORE_STATUS_UNINSTALLED,
] as const;

export type StoreStatus = (typeof STORE_STATUSES)[number];

export const LEGACY_CATALOG_PROGRESS_STORE_STATUS = "SYNCING";
export const NORMALIZED_LEGACY_CATALOG_PROGRESS_STORE_STATUS = "PENDING" satisfies StoreStatus;

export const RUNNABLE_STORE_STATUSES = [
  "PENDING",
  "ACTIVE",
] as const satisfies readonly StoreStatus[];

export function isStoreStatus(value: string): value is StoreStatus {
  return STORE_STATUSES.includes(value as StoreStatus);
}

export function isRunnableStoreStatus(status: StoreStatus): boolean {
  return RUNNABLE_STORE_STATUSES.includes(status as (typeof RUNNABLE_STORE_STATUSES)[number]);
}

export const MERCHANT_ROLE_OWNER = "OWNER";
export const MERCHANT_ROLE_APP_ADMIN = "APP_ADMIN";
export const MERCHANT_ROLE_PRICING_MANAGER = "PRICING_MANAGER";
export const MERCHANT_USER_STATUS_ACTIVE = "ACTIVE";
export const MERCHANT_USER_STATUS_SUSPENDED = "SUSPENDED";
export const MERCHANT_USER_STATUS_PENDING_APPROVAL = "PENDING_APPROVAL";

export const MERCHANT_ROLES = [
  MERCHANT_ROLE_OWNER,
  MERCHANT_ROLE_APP_ADMIN,
  MERCHANT_ROLE_PRICING_MANAGER,
] as const;
export type MerchantRole = (typeof MERCHANT_ROLES)[number];

export const MERCHANT_USER_STATUSES = [
  MERCHANT_USER_STATUS_PENDING_APPROVAL,
  MERCHANT_USER_STATUS_ACTIVE,
  MERCHANT_USER_STATUS_SUSPENDED,
] as const;
export type MerchantUserStatus = (typeof MERCHANT_USER_STATUSES)[number];

export const CUSTOMER_STATUSES = ["ACTIVE", "DISABLED", "UNKNOWN"] as const;
export type CustomerStatus = (typeof CUSTOMER_STATUSES)[number];

export const CUSTOMER_SOURCES = ["SHOPIFY_SYNC", "SHOPIFY_ON_DEMAND", "GUEST_CONTACT"] as const;
export type CustomerSource = (typeof CUSTOMER_SOURCES)[number];

export const PRODUCT_STATUS_ACTIVE = "ACTIVE" as const;
export const PRODUCT_STATUS_DELETED = "DELETED" as const;
export const PRODUCT_STATUSES = [
  PRODUCT_STATUS_ACTIVE,
  "DRAFT",
  "ARCHIVED",
  PRODUCT_STATUS_DELETED,
] as const;
export type ProductStatus = (typeof PRODUCT_STATUSES)[number];

export const COMPETITOR_STATUS_ALLOWED = "ALLOWED";
export const COMPETITOR_STATUS_BLOCKED = "BLOCKED";
export const COMPETITOR_STATUSES = [COMPETITOR_STATUS_ALLOWED, COMPETITOR_STATUS_BLOCKED] as const;
export type CompetitorStatus = (typeof COMPETITOR_STATUSES)[number];

export const COMPETITOR_REVIEW_STATUS_APPROVED = "APPROVED" as const;
export const COMPETITOR_REVIEW_STATUS_PENDING_REVIEW = "PENDING_REVIEW" as const;
export const COMPETITOR_REVIEW_STATUS_REJECTED = "REJECTED" as const;
export const COMPETITOR_REVIEW_STATUSES = [
  COMPETITOR_REVIEW_STATUS_APPROVED,
  COMPETITOR_REVIEW_STATUS_PENDING_REVIEW,
  COMPETITOR_REVIEW_STATUS_REJECTED,
] as const;
export type CompetitorReviewStatus = (typeof COMPETITOR_REVIEW_STATUSES)[number];

export const COMPETITOR_AUTOMATIC_MATCHING_ALLOW_AUTOMATIC_MATCH_DECISIONS =
  "ALLOW_AUTOMATIC_MATCH_DECISIONS";
export const COMPETITOR_AUTOMATIC_MATCHING_MANUAL_REVIEW_ONLY = "MANUAL_REVIEW_ONLY";
export const COMPETITOR_AUTOMATIC_MATCHING_MODES = [
  COMPETITOR_AUTOMATIC_MATCHING_ALLOW_AUTOMATIC_MATCH_DECISIONS,
  COMPETITOR_AUTOMATIC_MATCHING_MANUAL_REVIEW_ONLY,
] as const;
export type CompetitorAutomaticMatchingMode = (typeof COMPETITOR_AUTOMATIC_MATCHING_MODES)[number];

export const COMPETITOR_PRICE_MONITORING_ENABLED = "ENABLED";
export const COMPETITOR_PRICE_MONITORING_PAUSED = "PAUSED";
export const COMPETITOR_PRICE_MONITORING_STATUSES = [
  COMPETITOR_PRICE_MONITORING_ENABLED,
  COMPETITOR_PRICE_MONITORING_PAUSED,
] as const;
export type CompetitorPriceMonitoringStatus = (typeof COMPETITOR_PRICE_MONITORING_STATUSES)[number];

export const COMPETITOR_SUPPORT_LEVEL_SUPPORTED = "SUPPORTED";
export const COMPETITOR_SUPPORT_LEVEL_BLOCKED = "BLOCKED";
export const COMPETITOR_SUPPORT_LEVEL_UNKNOWN = "UNKNOWN";
export const COMPETITOR_SUPPORT_LEVELS = [
  COMPETITOR_SUPPORT_LEVEL_SUPPORTED,
  COMPETITOR_SUPPORT_LEVEL_BLOCKED,
  COMPETITOR_SUPPORT_LEVEL_UNKNOWN,
] as const;
export type CompetitorSupportLevel = (typeof COMPETITOR_SUPPORT_LEVELS)[number];

export const PRODUCT_MAPPING_STATUS_SUGGESTED = "SUGGESTED";
export const PRODUCT_MAPPING_STATUS_VERIFIED = "VERIFIED";
export const PRODUCT_MAPPING_STATUS_REJECTED = "REJECTED";
export const PRODUCT_MAPPING_STATUS_DISABLED = "DISABLED";
export const PRODUCT_MAPPING_STATUSES = [
  PRODUCT_MAPPING_STATUS_SUGGESTED,
  PRODUCT_MAPPING_STATUS_VERIFIED,
  PRODUCT_MAPPING_STATUS_REJECTED,
  PRODUCT_MAPPING_STATUS_DISABLED,
] as const;
export type ProductMappingStatus = (typeof PRODUCT_MAPPING_STATUSES)[number];

export const PRODUCT_MAPPING_ACTIVE_STATUSES = [
  PRODUCT_MAPPING_STATUS_SUGGESTED,
  PRODUCT_MAPPING_STATUS_VERIFIED,
] as const satisfies readonly ProductMappingStatus[];

export const PRODUCT_MAPPING_INACTIVE_STATUSES = [
  PRODUCT_MAPPING_STATUS_REJECTED,
  PRODUCT_MAPPING_STATUS_DISABLED,
] as const satisfies readonly ProductMappingStatus[];

export const PRODUCT_MAPPING_FRESHNESS_FRESH = "FRESH";
export const PRODUCT_MAPPING_FRESHNESS_STALE = "STALE";
export const PRODUCT_MAPPING_FRESHNESS_MISSING = "MISSING";
export const PRODUCT_MAPPING_FRESHNESS_FILTERS = [
  PRODUCT_MAPPING_FRESHNESS_FRESH,
  PRODUCT_MAPPING_FRESHNESS_STALE,
  PRODUCT_MAPPING_FRESHNESS_MISSING,
] as const;
export type ProductMappingFreshnessFilter = (typeof PRODUCT_MAPPING_FRESHNESS_FILTERS)[number];

export function isProductMappingStatus(value: string): value is ProductMappingStatus {
  return PRODUCT_MAPPING_STATUSES.includes(value as ProductMappingStatus);
}

export function isActiveProductMappingStatus(status: ProductMappingStatus): boolean {
  return PRODUCT_MAPPING_ACTIVE_STATUSES.includes(
    status as (typeof PRODUCT_MAPPING_ACTIVE_STATUSES)[number],
  );
}

export function isRequestBlockedByProductMappingStatus(status: ProductMappingStatus): boolean {
  return status === PRODUCT_MAPPING_STATUS_REJECTED;
}

export function isProductMappingFreshnessFilter(
  value: string,
): value is ProductMappingFreshnessFilter {
  return PRODUCT_MAPPING_FRESHNESS_FILTERS.includes(value as ProductMappingFreshnessFilter);
}

export const REQUESTER_TYPES = ["GUEST", "SHOPIFY_CUSTOMER", "CONTACT_ONLY"] as const;
export type RequesterType = (typeof REQUESTER_TYPES)[number];

export const PRICE_MATCH_REQUEST_STATUS_PROCESSING = "PROCESSING" as const;
export const PRICE_MATCH_REQUEST_STATUS_REVIEW_REQUIRED = "REVIEW_REQUIRED" as const;
export const PRICE_MATCH_REQUEST_STATUS_COMPLETED = "COMPLETED" as const;
export const PRICE_MATCH_REQUEST_STATUS_REJECTED = "REJECTED" as const;
export const PRICE_MATCH_REQUEST_STATUS_FAILED = "FAILED" as const;
export const PRICE_MATCH_REQUEST_STATUS_EXPIRED = "EXPIRED" as const;
export const STATUS_CANCELLED = "CANCELLED" as const;
export const PRICE_MATCH_REQUEST_STATUS_CANCELLED = STATUS_CANCELLED;

export const PRICE_MATCH_REQUEST_STATUSES = [
  PRICE_MATCH_REQUEST_STATUS_PROCESSING,
  PRICE_MATCH_REQUEST_STATUS_REVIEW_REQUIRED,
  PRICE_MATCH_REQUEST_STATUS_COMPLETED,
  PRICE_MATCH_REQUEST_STATUS_REJECTED,
  PRICE_MATCH_REQUEST_STATUS_FAILED,
  PRICE_MATCH_REQUEST_STATUS_EXPIRED,
  PRICE_MATCH_REQUEST_STATUS_CANCELLED,
] as const;
export type PriceMatchRequestStatus = (typeof PRICE_MATCH_REQUEST_STATUSES)[number];

export const OPEN_PRICE_MATCH_REQUEST_STATUSES = [
  PRICE_MATCH_REQUEST_STATUS_PROCESSING,
  PRICE_MATCH_REQUEST_STATUS_REVIEW_REQUIRED,
] as const satisfies readonly PriceMatchRequestStatus[];

export const TERMINAL_PRICE_MATCH_REQUEST_STATUSES = [
  PRICE_MATCH_REQUEST_STATUS_COMPLETED,
  PRICE_MATCH_REQUEST_STATUS_REJECTED,
  PRICE_MATCH_REQUEST_STATUS_FAILED,
  PRICE_MATCH_REQUEST_STATUS_EXPIRED,
  PRICE_MATCH_REQUEST_STATUS_CANCELLED,
] as const satisfies readonly PriceMatchRequestStatus[];

export function isOpenPriceMatchRequestStatus(status: PriceMatchRequestStatus): boolean {
  return OPEN_PRICE_MATCH_REQUEST_STATUSES.includes(
    status as (typeof OPEN_PRICE_MATCH_REQUEST_STATUSES)[number],
  );
}

export function isTerminalPriceMatchRequestStatus(status: PriceMatchRequestStatus): boolean {
  return TERMINAL_PRICE_MATCH_REQUEST_STATUSES.includes(
    status as (typeof TERMINAL_PRICE_MATCH_REQUEST_STATUSES)[number],
  );
}

export const PRICE_MATCH_PROCESSING_STAGE_REQUEST_RECEIVED = "REQUEST_RECEIVED" as const;
export const PRICE_MATCH_PROCESSING_STAGE_COMPETITOR_CHECKED = "COMPETITOR_CHECKED" as const;
export const PRICE_MATCH_PROCESSING_STAGE_EVIDENCE_CAPTURED = "EVIDENCE_CAPTURED" as const;
export const PRICE_MATCH_PROCESSING_STAGE_PRODUCT_MATCH_CHECKED = "PRODUCT_MATCH_CHECKED" as const;
export const PRICE_MATCH_PROCESSING_STAGE_OFFER_FACTS_CHECKED = "OFFER_FACTS_CHECKED" as const;
export const PRICE_MATCH_PROCESSING_STAGE_POLICY_CHECKED = "POLICY_CHECKED" as const;
export const PRICE_MATCH_PROCESSING_STAGE_DECISION_READY = "DECISION_READY" as const;

export const PRICE_MATCH_PROCESSING_STAGES = [
  PRICE_MATCH_PROCESSING_STAGE_REQUEST_RECEIVED,
  PRICE_MATCH_PROCESSING_STAGE_COMPETITOR_CHECKED,
  PRICE_MATCH_PROCESSING_STAGE_EVIDENCE_CAPTURED,
  PRICE_MATCH_PROCESSING_STAGE_PRODUCT_MATCH_CHECKED,
  PRICE_MATCH_PROCESSING_STAGE_OFFER_FACTS_CHECKED,
  PRICE_MATCH_PROCESSING_STAGE_POLICY_CHECKED,
  PRICE_MATCH_PROCESSING_STAGE_DECISION_READY,
] as const;
export type PriceMatchProcessingStage = (typeof PRICE_MATCH_PROCESSING_STAGES)[number];

export const PRICE_MATCH_OUTCOME_INSTANT_MATCH = "INSTANT_MATCH" as const;
export const PRICE_MATCH_OUTCOME_COUNTEROFFER = "COUNTEROFFER" as const;
export const PRICE_MATCH_OUTCOME_ALREADY_BETTER = "ALREADY_BETTER" as const;
export const PRICE_MATCH_OUTCOME_NO_OFFER = "NO_OFFER" as const;
export const PRICE_MATCH_OUTCOME_UNVERIFIABLE = "UNVERIFIABLE" as const;
export const PRICE_MATCH_OUTCOMES = [
  PRICE_MATCH_OUTCOME_INSTANT_MATCH,
  PRICE_MATCH_OUTCOME_COUNTEROFFER,
  PRICE_MATCH_OUTCOME_ALREADY_BETTER,
  PRICE_MATCH_OUTCOME_NO_OFFER,
  PRICE_MATCH_OUTCOME_UNVERIFIABLE,
] as const;
export type PriceMatchOutcome = (typeof PRICE_MATCH_OUTCOMES)[number];

export const VERIFICATION_DECISIONS = [
  "MATCH",
  "MISMATCH",
  "REVIEW_REQUIRED",
  "UNVERIFIABLE",
] as const;
export type VerificationDecision = (typeof VERIFICATION_DECISIONS)[number];

export const OFFER_TYPE_INSTANT_MATCH = "INSTANT_MATCH";
export const OFFER_TYPE_COUNTEROFFER = "COUNTEROFFER";
export const OFFER_TYPES = [OFFER_TYPE_INSTANT_MATCH, OFFER_TYPE_COUNTEROFFER] as const;
export type OfferType = (typeof OFFER_TYPES)[number];

export const OFFER_STATUS_CREATING = "CREATING";
export const OFFER_STATUS_AVAILABLE = "AVAILABLE";
export const OFFER_STATUS_ACCEPTED = "ACCEPTED";
export const OFFER_STATUS_ORDERED = "ORDERED";
export const OFFER_STATUS_EXPIRED = "EXPIRED";
export const OFFER_STATUS_REVOKED = "REVOKED";
export const OFFER_STATUS_FAILED = "FAILED";
export const OFFER_STATUSES = [
  OFFER_STATUS_CREATING,
  OFFER_STATUS_AVAILABLE,
  OFFER_STATUS_ACCEPTED,
  OFFER_STATUS_ORDERED,
  OFFER_STATUS_EXPIRED,
  OFFER_STATUS_REVOKED,
  OFFER_STATUS_FAILED,
] as const;
export type OfferStatus = (typeof OFFER_STATUSES)[number];

export type JobType =
  | "CATALOG_SYNC"
  | typeof CATALOG_REPAIR_JOB_TYPE
  | "CUSTOMER_SYNC"
  | "WEBHOOK_PROCESSING"
  | "PRICE_EXTRACTION"
  | typeof MAPPING_CHECK_JOB_TYPE
  | "EMAIL"
  | "CLEANUP"
  | "TOKEN_HEALTH";

export const JOB_STATUS_CANCELLED = STATUS_CANCELLED;
export const JOB_STATUS_QUEUED = "QUEUED" as const;
export const JOB_STATUS_RUNNING = "RUNNING" as const;
export const JOB_STATUS_SUCCEEDED = "SUCCEEDED" as const;
export const JOB_STATUS_FAILED = "FAILED" as const;
export const JOB_STATUSES = [
  JOB_STATUS_QUEUED,
  JOB_STATUS_RUNNING,
  JOB_STATUS_SUCCEEDED,
  JOB_STATUS_FAILED,
  JOB_STATUS_CANCELLED,
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export const TERMINAL_JOB_STATUSES = [
  JOB_STATUS_SUCCEEDED,
  JOB_STATUS_FAILED,
  JOB_STATUS_CANCELLED,
] as const satisfies readonly JobStatus[];
export type TerminalJobStatus = (typeof TERMINAL_JOB_STATUSES)[number];

export const SUCCESSFUL_JOB_STATUSES = [
  JOB_STATUS_SUCCEEDED,
] as const satisfies readonly TerminalJobStatus[];
export type SuccessfulJobStatus = (typeof SUCCESSFUL_JOB_STATUSES)[number];

export const CANCELLED_JOB_STATUSES = [
  JOB_STATUS_CANCELLED,
] as const satisfies readonly TerminalJobStatus[];
export type CancelledJobStatus = (typeof CANCELLED_JOB_STATUSES)[number];

export function isTerminalJobStatus(status: JobStatus): status is TerminalJobStatus {
  return TERMINAL_JOB_STATUSES.includes(status as TerminalJobStatus);
}

export function isSuccessfulJobStatus(status: JobStatus): status is SuccessfulJobStatus {
  return SUCCESSFUL_JOB_STATUSES.includes(status as SuccessfulJobStatus);
}

export function isCancelledJobStatus(status: JobStatus): status is CancelledJobStatus {
  return CANCELLED_JOB_STATUSES.includes(status as CancelledJobStatus);
}

export const NOTIFICATION_CHANNELS = ["IN_APP", "EMAIL"] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const NOTIFICATION_STATUS_PENDING = "PENDING" as const;
export const NOTIFICATION_STATUS_SENT = "SENT" as const;
export const NOTIFICATION_STATUS_READ = "READ" as const;
export const NOTIFICATION_STATUS_FAILED = "FAILED" as const;
export const NOTIFICATION_STATUSES = [
  NOTIFICATION_STATUS_PENDING,
  NOTIFICATION_STATUS_SENT,
  NOTIFICATION_STATUS_READ,
  NOTIFICATION_STATUS_FAILED,
] as const;
export type NotificationStatus = (typeof NOTIFICATION_STATUSES)[number];

export const PRODUCT_CONDITIONS = ["NEW", "USED", "REFURBISHED", "OPEN_BOX", "UNKNOWN"] as const;
export type ProductCondition = (typeof PRODUCT_CONDITIONS)[number];

export const AVAILABILITY_IN_STOCK = "IN_STOCK";
export const AVAILABILITY_OUT_OF_STOCK = "OUT_OF_STOCK";
export const AVAILABILITY_UNKNOWN = "UNKNOWN";
export const AVAILABILITY_STATUSES = [
  AVAILABILITY_IN_STOCK,
  AVAILABILITY_OUT_OF_STOCK,
  AVAILABILITY_UNKNOWN,
] as const;
export type Availability = (typeof AVAILABILITY_STATUSES)[number];

export const IDENTIFIER_TYPES = ["SKU", "GTIN", "MPN"] as const;
export type IdentifierType = (typeof IDENTIFIER_TYPES)[number];

export const AUDIT_ACTOR_TYPES = ["MERCHANT_USER", "SYSTEM_ADMIN", "SHOPIFY", "SYSTEM"] as const;
export type AuditActorType = (typeof AUDIT_ACTOR_TYPES)[number];
export const AUDIT_ACTOR_MERCHANT_USER: AuditActorType = AUDIT_ACTOR_TYPES[0];
export const AUDIT_ACTOR_SYSTEM: AuditActorType = AUDIT_ACTOR_TYPES[3];

// Internal Price Rescue system admin roles — separate from merchant store roles (MerchantRole)
export const SYSTEM_ADMIN_ROLES = ["SYSTEM_ADMIN", "SUPPORT_ADMIN", "READ_ONLY_ADMIN"] as const;
export type SystemAdminRole = (typeof SYSTEM_ADMIN_ROLES)[number];

export const SYSTEM_ADMIN_STATUSES = ["ACTIVE", "SUSPENDED", "REVOKED"] as const;
export type SystemAdminStatus = (typeof SYSTEM_ADMIN_STATUSES)[number];

export const SYSTEM_ADMIN_SESSION_STATUSES = ["ACTIVE", "REVOKED", "SUSPENDED"] as const;
export type SystemAdminSessionStatus = (typeof SYSTEM_ADMIN_SESSION_STATUSES)[number];

export const SUPPORT_ACCESS_STATUSES = ["ACTIVE", "EXPIRED", "REVOKED"] as const;
export type SupportAccessStatus = (typeof SUPPORT_ACCESS_STATUSES)[number];

export const AUTH_FLOW_TYPES = ["SHOPIFY_INSTALL", "ADMIN_LOGIN"] as const;
export type AuthFlowType = (typeof AUTH_FLOW_TYPES)[number];

export const IDEMPOTENCY_STATUSES = ["IN_PROGRESS", "COMPLETED"] as const;
export type IdempotencyStatus = (typeof IDEMPOTENCY_STATUSES)[number];

export const CREDENTIAL_REFRESH_STATUS_CURRENT = "CURRENT";
export const CREDENTIAL_REFRESH_STATUS_REFRESHING = "REFRESHING";
export const CREDENTIAL_REFRESH_STATUS_REAUTHORIZATION_REQUIRED = "REAUTHORIZATION_REQUIRED";
export const CREDENTIAL_REFRESH_STATUS_FAILED = "FAILED";
export const CREDENTIAL_REFRESH_STATUSES = [
  CREDENTIAL_REFRESH_STATUS_CURRENT,
  CREDENTIAL_REFRESH_STATUS_REFRESHING,
  CREDENTIAL_REFRESH_STATUS_REAUTHORIZATION_REQUIRED,
  CREDENTIAL_REFRESH_STATUS_FAILED,
] as const;
export type CredentialRefreshStatus = (typeof CREDENTIAL_REFRESH_STATUSES)[number];
